import { useQuery } from "@tanstack/react-query"
import { JwtPayload, jwtDecode } from "jwt-decode"

import { supabase } from "@/lib/supabase"

export interface SessionInvalidation {
  id: string
  user_id: string | null
  invalidate_at: string
  is_active: boolean
  created_at: string
  created_by: string | null
  reason: string | null
}

/**
 * Get the latest processed invalidation ID from localStorage for a specific user
 */
function getProcessedInvalidation(userId: string): string | null {
  if (typeof window === "undefined") {
    return null
  }

  try {
    const key = `session-invalidations-processed-${userId}`
    const stored = localStorage.getItem(key)
    if (stored) {
      return stored
    }
  } catch (error) {
    console.error("Error reading processed invalidation from localStorage:", error)
  }

  return null
}

/**
 * Hook to check if the current user's session should be invalidated
 * Queries for active invalidations that affect the current user:
 * - Global invalidations (user_id is null) where invalidate_at <= now()
 * - User-specific invalidations where user_id = current_user_id and invalidate_at <= now()
 * Filters out invalidations that have already been processed (logged out) for this user
 * Only applies invalidations that were created AFTER the session was issued (to prevent new users from being logged out by pre-existing invalidations)
 *
 * @returns Query result with invalidation data and a boolean indicating if session should be invalidated
 */
export function useSessionInvalidation() {
  return useQuery<{ shouldInvalidate: boolean; invalidation: SessionInvalidation | null }>({
    queryKey: ["session-invalidation"],
    queryFn: async () => {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession()

      if (!currentSession?.user) {
        return { shouldInvalidate: false, invalidation: null }
      }

      // Get the latest processed invalidation ID for this user
      const processedId = getProcessedInvalidation(currentSession.user.id)

      // Extract session creation time from JWT token's 'iat' (issued at) claim
      let sessionIssuedAt: number | null = null
      try {
        const decoded = jwtDecode<JwtPayload>(currentSession.access_token)
        if (decoded.iat) {
          // Convert to milliseconds for comparison with Date timestamps
          sessionIssuedAt = decoded.iat * 1000
        }
      } catch (error) {
        console.error("Error decoding JWT token:", error)
        // If we can't decode the token, we'll skip the time comparison
        // This is a fallback to ensure the feature still works
      }

      const now = new Date().toISOString()

      // Query for the latest active invalidation affecting this user
      // 1. Global invalidations (user_id is null)
      // 2. User-specific invalidations for this user
      const { data, error } = await supabase
        .from("session_invalidations")
        .select("*")
        .eq("is_active", true)
        .lte("invalidate_at", now)
        .or(`user_id.is.null,user_id.eq.${currentSession.user.id}`)
        .order("invalidate_at", { ascending: false })
        .limit(1)

      if (error) {
        console.error("Error checking session invalidation:", error)
        return { shouldInvalidate: false, invalidation: null }
      }

      // Get the latest invalidation (if any)
      const invalidation = data && data.length > 0 ? data[0] : null

      if (!invalidation) {
        return { shouldInvalidate: false, invalidation: null }
      }

      // Check if this invalidation has already been processed
      if (invalidation.id === processedId) {
        return { shouldInvalidate: false, invalidation: null }
      }

      // Only invalidate if the invalidation was created AFTER the session was issued
      // This prevents new users from being logged out by pre-existing invalidations
      let shouldInvalidate = true

      if (sessionIssuedAt !== null) {
        const invalidationCreatedAt = new Date(invalidation.created_at).getTime()
        // Only invalidate if invalidation was created after session was issued
        shouldInvalidate = invalidationCreatedAt > sessionIssuedAt

        if (!shouldInvalidate) {
          // Invalidation was created before session, so it shouldn't affect this session
          // This is expected for new users who activate their account after an invalidation was created
          return { shouldInvalidate: false, invalidation: null }
        }
      }
      // If we couldn't decode the session timestamp, we'll still check invalidations
      // This is a fallback to ensure security (better to be safe)

      return {
        shouldInvalidate,
        invalidation: invalidation as SessionInvalidation | null,
      }
    },
    // Poll every 60 seconds to check for invalidations
    refetchInterval: 60 * 1000,
    // Also refetch when window becomes visible
    refetchOnWindowFocus: true,
    // Keep data fresh
    staleTime: 0,
  })
}
